

import type { UndoProvenance } from "./undo.js";

export interface IUndoRedoElement {
    /**
     * A human-readable name for the action.
     */
    readonly label: string;

    /**
     * A code describing the action (for localization or categorization purposes).
     */
    readonly code?: string;

    /** Stable producer identity for history display and future collaboration. */
    readonly sourceId?: number;
    readonly provenance?: UndoProvenance;
    readonly timestamp?: number;


    undo: () => void
    perform: () => void

    /**
     * Called when the entry is permanently dropped from the undo/redo history
     * (history cleared, entry evicted past the stack limit, or redo
     * invalidated by a new action) — release objects retained for undo here.
     */
    dispose?: () => void

    /**
     * Optional app data for HISTORY-LIFECYCLE consumers: the undo service fires
     * `onDidDisposeElement` when an entry permanently leaves history; consumers
     * identify what the entry was about through this (e.g. a delete entry carries
     * its deleted object so the session can release the file once the delete is
     * final).
     */
    readonly data?: unknown
}

export class CompundUndoRedo implements IUndoRedoElement {
    private readonly actions: IUndoRedoElement[] = [];

    constructor(init: {
        label: string,
        actions: IUndoRedoElement[]
    }) {
        this._label = init.label;
        this.actions = [...init.actions];
    }
    private readonly _label: string;
    get label(): string {
        return this._label;
    }

    async undo(): Promise<void> {
        for (let i = this.actions.length - 1; i >= 0; i--) {
            await this.actions[i].undo();
        }
    }
    async perform(): Promise<void> {
        for (const action of this.actions) {
            await action.perform();
        }
    }
    dispose() {
        for (const action of this.actions) {
            action.dispose?.();
        }
    }
}

export class BaseAction implements IUndoRedoElement {

    private readonly _label: string;
    readonly undo: () => void;
    readonly perform: () => void;
    readonly dispose?: () => void;
    readonly sourceId?: number;
    readonly provenance?: UndoProvenance;
    readonly timestamp?: number;

    constructor(init: {
        label: string,
        undo: () => void,
        perform: () => void,
        dispose?: () => void,
        sourceId?: number,
        provenance?: UndoProvenance,
        timestamp?: number,
    }) {
        this._label = init.label;
        this.undo = init.undo;
        this.perform = init.perform;
        this.dispose = init.dispose;
        this.sourceId = init.sourceId;
        this.provenance = init.provenance;
        this.timestamp = init.timestamp;
    }

    get label(): string {
        return this._label;
    }
}
